import type { JSX } from 'react';
import React from 'react';
import { useMemo } from 'react';
import { DrawApi } from '@apply/api';
import { ApprovedCheckbox } from './ApprovedCheckbox';
import { EquityMetOn } from './EquityMetOn';
import { EquityRequirement } from './EquityRequirement';
import { BankAccountLast4Digits } from './BankAccountLast4Digits';
import { isMobileScreen } from '../../../../helpers';
import { EquityRemaining } from './EquityRemaining';

interface IHeaderPanelCenterProps {
    onEquityRequirementChange: (newValue: number) => void;
    onEquityMetOnChange: (newValue: Date) => void;
    onBankAccountLast4DigitsOnChange: (newValue: string) => void;
    onApprovalAdd: () => void;
    drawBudget?: DrawApi.IDrawBudget;
    equityAmount: number;
    isAdmin: boolean;
    anyAcquisitionAmount: boolean;
    isReadOnly?: boolean;
}

export const HeaderPanelCenter = ({
    onEquityRequirementChange,
    onEquityMetOnChange,
    onBankAccountLast4DigitsOnChange,
    onApprovalAdd,
    drawBudget,
    equityAmount,
    isAdmin,
    anyAcquisitionAmount,
    isReadOnly = false,
}: IHeaderPanelCenterProps): JSX.Element => {
    const headerWidth = useMemo(() => {
        let width = 300;
        if (isAdmin) {
            width += 300;
        } else {
            if (anyAcquisitionAmount) {
                width += 100;
            }
        }
        return width;
    }, [isAdmin, anyAcquisitionAmount]);

    // Checking window width to apply mobile style
    const getStyle = () => {
        const mobileScreen = isMobileScreen();
        const style = mobileScreen ? { display: 'none' } : { width: headerWidth };
        return style;
    };

    return (
        <div
            className='draw-header-center draw-header-panel'
            style={getStyle()}
        >
            <div>
                <div className='draw-header-input-group'>
                    <div className='draw-header-input-group-column'>
                        <div className='draw-header-budget-header-label'>Budget</div>
                        <div>
                            {isAdmin && (
                                <ApprovedCheckbox
                                    drawBudget={drawBudget}
                                    isAdmin={isAdmin}
                                    onApprovalAdd={onApprovalAdd}
                                    isReadOnly={isReadOnly}
                                />
                            )}
                        </div>
                    </div>
                    <div className='draw-header-input-group-column'>
                        <div>
                            <EquityRequirement
                                drawBudget={drawBudget}
                                isAdmin={isAdmin}
                                onEquityRequirementChange={onEquityRequirementChange}
                                isReadOnly={isReadOnly}
                            />
                        </div>
                        <div>
                            <EquityRemaining
                                drawBudget={drawBudget}
                                equityAmount={equityAmount}
                                isAdmin={isAdmin}
                            />
                        </div>
                    </div>
                    <div className='draw-header-input-group-column'>
                        <div>
                            <EquityMetOn
                                drawBudget={drawBudget}
                                isAdmin={isAdmin}
                                onEquityMetOnChange={onEquityMetOnChange}
                                isReadOnly={isReadOnly}
                            />
                        </div>
                        <div>
                            {isAdmin && (
                                <div title={isReadOnly ? 'This Budget is read only.' : ''}>
                                    <label htmlFor='bankAccountLast4Digits'>Bank Acc. (Last 4 Digits)</label>
                                    <BankAccountLast4Digits
                                        id={'bankAccountLast4Digits'}
                                        onChange={onBankAccountLast4DigitsOnChange}
                                        value={drawBudget?.bankAccountLast4Digits}
                                        isReadOnly={isReadOnly}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
